import * as React from "react";
import {translate} from "react-i18next";

// redux
import {connect} from "react-redux";

// router
import {RouteComponentProps} from "react-router-dom";

// services
import {
    Link,
    GroupDto,
    RoleDto,
    ResourceListOfGroupDto,
    ResourceListOfRoleDto, MenuDto, ResourceListOfModelyearDto, CnrDto, AssignedGroupRoleDto,
} from "../../../services/Client";

// components
import Button from "../../../components/Button";
import TextField from "../../../components/TextField";

import {getDtoArray} from "../AdminUtils";
import GroupsTable from "./GroupsTable";
import GroupCreationDialog from "./GroupCreationDialog";
import {renderErrorToast} from "../../../utils/FeedbackMessageUtils";
import GeneralActions from "../../../redux/actions/GeneralActions";
import AdminViewActions from "../../../redux/actions/AdminViewActions";
import {Checkbox, Dropdown, Icon} from "semantic-ui-react";
import HATEOASClient from "../../../services/HATEOASClient";
import {ITranslatedComponent, ITypefamilyMap} from "../../../redux/reducers/generalReducer";
import {compose, Dispatch} from "redux";
import {
    selectClient,
    selectCnrList,
    selectModelyears,
    selectTypefamilyMap,
    selectAdminArealinks,
    selectGroups,
    selectRoles
} from "../../../redux/selectors";
import {IReduxState} from "src/redux/IReduxState";
import {selectAccessFilter} from "../../../redux/selectors/administrationSelectors";

enum AccessRightsFilter {
    SHOW_ALL = 1,
    ACCESS_RIGHTS = 2,
    NO_ACCESS_RIGHTS = 3
}

interface IMapStateToProps {
    outerFilter?: string;
    adminAreaLinks?: MenuDto;
    client: HATEOASClient;
    groups?: ResourceListOfGroupDto;
    roles?: ResourceListOfRoleDto;
    modelYears?: ResourceListOfModelyearDto;
    typefamilyMap?: ITypefamilyMap;
    cnrList?: CnrDto[];
}

interface IMapDispatchToProps {
    addTranslationLocation: () => void;
    receiveGroups: (groups: ResourceListOfGroupDto) => void;
    receiveRoles: (roles: ResourceListOfRoleDto) => void;
}

interface IOwnProps {

}

interface IGroupsViewProps extends IOwnProps, IMapStateToProps, IMapDispatchToProps, ITranslatedComponent, RouteComponentProps<any> {
}

interface IGroupsViewState {
    /** Whether or not we are waiting for a response from the back end. */
    loadingGroups: boolean;

    /** Whether or not we are waiting for a response from the back end. */
    loadingRoles: boolean;

    /** Group name filter. */
    filter: string | undefined;

    /** Groups that match the current filter. */
    filteredGroups: GroupDto[];

    /** Whether or not to show the group creation dialog. */
    showGroupCreationDialog: boolean;

    /** Flag for displaying/hiding the access view rights for all groups  */
    showAllViewAccessRights: boolean;

    /** Filter based on group view access rights */
    accessRightsFilter: AccessRightsFilter;

    /** All roles that are assigned to groups */
    filterValues: Map<number, RoleDto>;

    /** Filter based on group view roles */
    roleFilter: string[];

    roleFilterAllSelected: boolean;
}

class GroupsView extends React.Component<IGroupsViewProps, IGroupsViewState> {
    constructor(props: IGroupsViewProps) {
        super(props);

        this.state = {
            loadingGroups: false,
            loadingRoles: false,
            filter: undefined,
            filteredGroups: getDtoArray<GroupDto>(this.props.groups),
            showGroupCreationDialog: false,
            showAllViewAccessRights: false,
            accessRightsFilter: AccessRightsFilter.SHOW_ALL,
            filterValues: this.renderFilterValues(),
            roleFilter: [],
            roleFilterAllSelected: false
        };
    }

    componentDidMount() {
        this.props.addTranslationLocation();

        if (!this.props.groups) {
            // we need the ResourceListOfGroupDto to determine if the current user has
            // the rights to create new groups and pass the groups into group creation dialog
            // and the groups table.
            this.requestGroups();
        }

        if (!this.props.roles) {
            // we need a list of all available roles for the group creation dialog
            // and roles selection in the groups table.
            this.requestRoles();
        }

        if (this.props.outerFilter !== undefined) {
            this.setState({filter: this.props.outerFilter});
        }
    }

    componentDidUpdate( prevProps: IGroupsViewProps, previousState: IGroupsViewState) {
        if (this.state.filter !== previousState.filter) {
            const filterValue = this.state.filter !== "" ? this.state.filter : undefined;
            let filteredDtos = this.applyFilter(getDtoArray<GroupDto>(this.props.groups), filterValue, this.state.accessRightsFilter);

            this.setState({filter: filterValue, filteredGroups: filteredDtos});
        }

        if (this.props.groups !== prevProps.groups) {
            this.setState({filterValues: this.renderFilterValues()});
        }
    }

    requestGroups = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_groupList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingGroups: true});
        this.props.client.GET_groupList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfGroupDto) => {
                this.props.receiveGroups(value);

                // request was successfully fulfilled; reset loading state
                this.setState({
                    loadingGroups: false,
                    filteredGroups: this.applyFilter(value.elements || [], this.state.filter, this.state.accessRightsFilter)
                });
                this.renderFilterValues();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_GROUPS",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingGroups: false
                });
            });
    }

    requestRoles = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_roleList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingRoles: true});
        this.props.client.GET_roleList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfRoleDto) => {
                this.props.receiveRoles(value);

                // request was successfully fulfilled; reset loading state
                this.setState({loadingRoles: false});
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_ROLES",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingRoles: false
                });
            });
    }

    addGroup = (group: GroupDto): void => {
        this.setState({loadingGroups: true});

        if (this.props.groups) {
            this.props.client.POST_group(this.props.groups).execute(group)
                .then((value: Link) => {
                    // group was created succesfully; refresh the redux store.
                    // requestGroups() takes care of resetting the loadingGroups state.
                    this.requestGroups();
                })
                .catch((reason: any) => {
                    window.scrollTo(0, 0);
                    renderErrorToast({
                        message: "ERRORS:CREATE_GROUP",
                        translationOptions: {groupName: group.groupName},
                        details: reason,
                        timestamp: Date.now()
                    });
                    this.setState({
                        loadingGroups: false
                    });
                });
        }
    }

    updateGroup = (oldGroup: GroupDto, newGroup: GroupDto): void => {
        this.setState({loadingGroups: true});

        this.props.client.PATCH_group(oldGroup).execute(newGroup)
            .then((value: Link) => {
                // group was updated succesfully; refresh the redux store.
                // requestGroups() takes care of resetting the loadingGroups state.
                this.requestGroups();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:UPDATE_GROUP",
                    translationOptions: {groupName: oldGroup.groupName},
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingGroups: false
                });
            });
    }

    deleteGroup = (group: GroupDto): void => {
        this.setState({loadingGroups: true});

        this.props.client.DELETE_group(group).execute(group)
            .then((value: any) => {
                // group was deleted succesfully; refresh the redux store.
                // requestGroups() takes care of resetting the loadingGroups state.
                this.requestGroups();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:DELETE_GROUP",
                    translationOptions: {groupName: group.groupName},
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingGroups: false
                });
            });
    }

    applyFilter = (dtos: GroupDto[], groupNamefilter: string | undefined, accessRightsFilter: AccessRightsFilter): GroupDto[] => {
        let filteredDtos = dtos;

        if (groupNamefilter) {
            const filterLowerCase = groupNamefilter.toLowerCase();
            filteredDtos = filteredDtos.filter(dto => dto.groupName.toLowerCase().includes(filterLowerCase));
        }

        if (accessRightsFilter !== AccessRightsFilter.SHOW_ALL) {
            filteredDtos = filteredDtos.filter(dto =>
                ((dto.assignedCnrs !== undefined && dto.assignedCnrs.length > 0) ||
                    (dto.assignedModelyears !== undefined && dto.assignedModelyears.length > 0)) === (accessRightsFilter === AccessRightsFilter.ACCESS_RIGHTS));
        }

        return filteredDtos;
    }

    applyRoleFilter = (dtos: GroupDto[], groupNameFilter: string | undefined, roleFilter: string[]): GroupDto[] => {
        let filteredDtos = dtos;

        if (groupNameFilter) {
            const filterLowerCase = groupNameFilter.toLowerCase();
            filteredDtos = filteredDtos.filter(dto => dto.groupName.toLowerCase().includes(filterLowerCase));
        }

        if (roleFilter.length > 0) {
            filteredDtos = filteredDtos.filter((dto) => {
                let test: boolean = false;
                if(dto.assignedRoles && dto.assignedRoles.length > 0) {
                    test = dto.assignedRoles.some((assignedRole) => assignedRole.role
                        ? roleFilter.some(filter => assignedRole.role ? assignedRole.role.name === filter : false)
                        : false
                    );
                }
                return test;
            });
        }

        return filteredDtos;
    }
    renderFilters = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_NAME")}
            value={this.state.filter}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                this.setState({filter: filterValue});
            }}
        />
    )

    renderTable = (loading: boolean, groups: GroupDto[], roles: RoleDto[]): JSX.Element => (
        <GroupsTable
            groups={groups}
            roles={roles}
            modelyears={this.props.modelYears && this.props.modelYears.elements ? this.props.modelYears.elements : []}
            typefamiliesMap={this.props.typefamilyMap || {}}
            cnrs={this.props.cnrList ? this.props.cnrList : []}
            loading={loading}
            showAllViewAccessRights={this.state.showAllViewAccessRights}
            optionalControls={{
                canGetGroupDetails: dto => this.props.client.GET_groupDetails(dto).canExecute(),
                canUpdateGroup: dto => this.props.client.PATCH_group(dto).canExecute(),
                canDeleteGroup: dto => this.props.client.DELETE_group(dto).canExecute(),
            }}
            onUpdateGroup={(oldDto: GroupDto, newDto: GroupDto) => {
                this.updateGroup(oldDto, newDto);
            }}
            onDeleteGroup={this.deleteGroup}
        />
    )

    renderRefreshBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("RELOAD")}
            labelPosition="after"
            icon={"refresh"}
            disabled={loading}
            onClick={() => {
                this.requestGroups();
                this.requestRoles();
            }}
        />
    )

    renderAddGroupBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("ADD_GROUP")}
            labelPosition="after"
            icon={"add"}
            disabled={loading}
            onClick={() => {
                this.setState({showGroupCreationDialog: true});
            }}
        />
    )

    renderAddGroupDlg = (groups: GroupDto[], roles: RoleDto[]): JSX.Element => (
        <GroupCreationDialog
            groups={groups}
            roles={roles}
            onClose={(newGroup?: GroupDto) => {
                this.setState({showGroupCreationDialog: false});
                if (newGroup) {
                    this.addGroup(newGroup);
                }
            }}
        />
    )

    renderShowAllViewAccessRightsBtn = (): JSX.Element => (
        <Button
            label={this.props.t(this.state.showAllViewAccessRights ? "HIDE_ALL_VIEW_ACCESS_RIGHTS" : "SHOW_ALL_VIEW_ACCESS_RIGHTS")}
            labelPosition={"after"}
            icon={this.state.showAllViewAccessRights ? "hide" : "unhide"}
            disabled={this.state.loadingGroups}
            onClick={() => this.setState({showAllViewAccessRights: !this.state.showAllViewAccessRights})}
        />
    )

    renderAccessRightsFilter = (): JSX.Element => (
        <Dropdown
            id={'groups-access-rights-filter'}
            clearable={false}
            closeOnChange={true}
            selection={true}
            disabled={this.state.loadingGroups}
            className={this.state.accessRightsFilter === AccessRightsFilter.ACCESS_RIGHTS ? 'groups-active-access-rights'
                : `color--white ${this.state.accessRightsFilter === AccessRightsFilter.NO_ACCESS_RIGHTS ? 'font-weight--bold' : ''}`}
            icon={<Icon name={'filter'} style={{float: 'left'}} />}
            options={[
                {
                    key: AccessRightsFilter.SHOW_ALL,
                    text: this.props.t("SHOW_ALL_GROUPS"),
                    value: AccessRightsFilter.SHOW_ALL
                },
                {
                    key: AccessRightsFilter.ACCESS_RIGHTS,
                    text: this.props.t("ACCESS_RIGHTS_GROUPS"),
                    value: AccessRightsFilter.ACCESS_RIGHTS
                },
                {
                    key: AccessRightsFilter.NO_ACCESS_RIGHTS,
                    text: this.props.t("NO_ACCESS_RIGHTS_GROUPS"),
                    value: AccessRightsFilter.NO_ACCESS_RIGHTS
                }
            ]}
            value={this.state.accessRightsFilter}
            onChange={(e, v) => {
                const newARFilter = v.value as AccessRightsFilter;
                let filteredDtos = this.applyFilter(getDtoArray<GroupDto>(this.props.groups), this.state.filter, newARFilter);

                this.setState({filteredGroups: filteredDtos, accessRightsFilter: newARFilter});
            }}
        />
    )

    renderRolesFilter = (): JSX.Element => (
        <Dropdown
            id={'access-tab-role-filter'}
            icon={<Icon name={'filter'} style={{float: 'left'}} />}
            text={this.props.t("ROLES")}
            disabled={this.state.accessRightsFilter === AccessRightsFilter.NO_ACCESS_RIGHTS}
            scrolling={true}
        >
            <Dropdown.Menu>
                <Dropdown.Item
                    onClick={(e) => {
                        let {roleFilter} = this.state;
                        const filterValues = Array.from(this.state.filterValues.values());

                        e.stopPropagation();

                        this.state.roleFilterAllSelected
                            ? roleFilter = []
                            : filterValues.forEach(filter => roleFilter.push(filter.name));

                        let filteredDtos = this.applyRoleFilter(getDtoArray<GroupDto>(this.props.groups), this.state.filter, roleFilter);

                        this.setState({filteredGroups: filteredDtos, roleFilter: roleFilter, roleFilterAllSelected: !this.state.roleFilterAllSelected});
                    }}
                >
                    <Checkbox
                        checked={this.state.roleFilterAllSelected}
                        label={this.props.t("GLOBAL:SELECT_ALL")}
                        className={'disableClickEvents'}
                    />
                </Dropdown.Item>
                <Dropdown.Header
                    icon='tags'
                    content={this.props.t("GLOBAL:FILTER_ROLES")}
                />
                {this.state.filterValues && Array.from(this.state.filterValues.values()).sort((a, b) => a.name.localeCompare(b.name)).map((key: RoleDto) => {
                    return (
                        <Dropdown.Item
                            key={key.objectId}
                            onClick={(e) => {
                                let {roleFilter} = this.state;

                                e.stopPropagation();

                                !roleFilter.includes(key.name)
                                    ? roleFilter.push(key.name)
                                    : roleFilter = roleFilter.filter(filter => filter !== key.name);

                                const roleFilterAllSelected = roleFilter.length === this.state.filterValues.size ? true : false;

                                let filteredDtos = this.applyRoleFilter(getDtoArray<GroupDto>(this.props.groups), this.state.filter, roleFilter);

                                this.setState({filteredGroups: filteredDtos, roleFilter, roleFilterAllSelected});
                            }}
                        >
                            <Checkbox
                                checked={this.state.roleFilter.includes(key.name)}
                                label={key.name}
                                className={'disableClickEvents'}
                            />
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        </Dropdown>
    )

    renderFilterValues(): Map<number, RoleDto> {
        let filterValues: Map<number, RoleDto> = new Map<number, RoleDto>();
        getDtoArray<GroupDto>(this.props.groups).forEach((group: GroupDto) => {
            if (group.assignedRoles) {
                group.assignedRoles.forEach((assignedRole: AssignedGroupRoleDto) => {
                    if (assignedRole.role) {
                        if (!filterValues.has(assignedRole.role.objectId)) {
                            filterValues.set(assignedRole.role.objectId, assignedRole.role);
                        }
                    }
                });
            }
        });

        return filterValues;
    }

    render() {
        const groups = getDtoArray<GroupDto>(this.props.groups);
        const roles = getDtoArray<RoleDto>(this.props.roles);
        const filteredGroups = this.state.filteredGroups;

        return (
            <>
                <div style={{display: 'flex'}}>
                    {this.props.groups &&
                        this.props.client.POST_group(this.props.groups).canExecute() &&
                        this.renderAddGroupBtn(this.state.loadingGroups)
                    }
                    {// user has WRITE rights
                        this.props.groups &&
                        this.props.client.POST_group(this.props.groups).canExecute() &&
                        this.renderShowAllViewAccessRightsBtn()
                    }
                    {this.props.groups && this.renderAccessRightsFilter()}
                    {this.props.groups && this.renderRolesFilter()}
                    {this.renderRefreshBtn(this.state.loadingGroups)}
                </div>
                <br />
                {this.renderFilters()}
                {this.renderTable(this.state.loadingGroups || this.state.loadingRoles, filteredGroups, roles)}
                {
                    this.state.showGroupCreationDialog &&
                    this.renderAddGroupDlg(groups, roles)
                }
            </>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    outerFilter: selectAccessFilter(state),
    adminAreaLinks: selectAdminArealinks(state),
    client: selectClient(state),
    groups: selectGroups(state),
    roles: selectRoles(state),
    modelYears: selectModelyears(state),
    typefamilyMap: selectTypefamilyMap(state),
    cnrList: selectCnrList(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>): IMapDispatchToProps => ({
    addTranslationLocation: () => dispatch(GeneralActions.AddTranslationLocation({pattern: "admin/groups", substitutes: "Groups Management"})),
    receiveGroups: (groups: ResourceListOfGroupDto) => dispatch(AdminViewActions.ReceiveGroups(groups)),
    receiveRoles: (roles: ResourceListOfRoleDto) => dispatch(AdminViewActions.ReceiveRoles(roles)),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate("ADMIN_VIEW"),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(GroupsView);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/GroupsView.tsx